import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import type { PartnerProfile } from '../../../services/modules/partners/partnerApi';
import type { PartnerStatus } from '../../../domain/partners/types';

type PartnerRow = {
  id: string;
  code: string;
  status: PartnerStatus;
  commission_rate: number;
  company_name: string;
  connect_onboarding_complete: boolean;
  created_at: string;
};

function mapPartner(row: PartnerRow): PartnerProfile {
  return {
    id: row.id,
    code: row.code,
    status: row.status,
    commissionRate: Number(row.commission_rate) || 0.25,
    companyName: row.company_name,
    connectOnboardingComplete: row.connect_onboarding_complete,
    crmAccessGranted: false,
    createdAt: row.created_at,
  };
}

/**
 * UX only — partner row via RLS (own user_id).
 * Do not use Edge Functions here: a network/CORS failure must not look like "not a partner".
 */
export function useIsPartner() {
  const { user, isAuthLoading } = useAuth();
  const [partner, setPartner] = useState<PartnerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) {
      setPartner(null);
      setLoadFailed(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const { data, error } = await supabase
      .from('partners')
      .select(
        'id, code, status, commission_rate, company_name, connect_onboarding_complete, created_at',
      )
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error('[useIsPartner]', error.message);
      setPartner(null);
      setLoadFailed(true);
    } else {
      setPartner(data ? mapPartner(data as PartnerRow) : null);
      setLoadFailed(false);
    }
    setIsLoading(false);
  }, [user?.id]);

  useEffect(() => {
    if (isAuthLoading) return;
    void load();
  }, [isAuthLoading, load]);

  return {
    partner,
    isPartner: Boolean(partner),
    isActivePartner: partner?.status === 'active',
    isLoading: isAuthLoading || isLoading,
    loadFailed,
    refetch: load,
  };
}
